import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import { alpha, keyframes } from '@mui/material/styles'

/**
 * The surface anything waiting for a label is shown on: a viewfinder.
 *
 * The four brackets are the finder pattern of the labels being read, so "aim here" is said with the
 * shape the operator is already holding rather than with a dashed grey box, which reads as a
 * placeholder. The sweep is the one moving part and it means one single thing — the reader is
 * listening; it stops for anyone whose system asks for less movement, and for a caller that is busy.
 *
 * It lives in generic/qr because the reading dialog and the MOS widget both show it: the widget's
 * empty state and the dialog's waiting state are the same moment of the same gesture, and an operator
 * who learns the frame in one place must recognise it in the other.
 *
 * `framed` off keeps the surface and drops the art, for a caller with a single line of room.
 */

const sweep = keyframes({
  '0%': { top: '0%', opacity: 0 },
  '25%': { opacity: 1 },
  '75%': { opacity: 1 },
  '100%': { top: '100%', opacity: 0 }
})

// One bracket per corner, each drawing the two edges that meet there.
const CORNERS = [
  { id: 'topLeft', top: 6, left: 6, borderTopWidth: 2, borderLeftWidth: 2, borderTopLeftRadius: '0.35rem' },
  { id: 'topRight', top: 6, right: 6, borderTopWidth: 2, borderRightWidth: 2, borderTopRightRadius: '0.35rem' },
  { id: 'bottomLeft', bottom: 6, left: 6, borderBottomWidth: 2, borderLeftWidth: 2, borderBottomLeftRadius: '0.35rem' },
  { id: 'bottomRight', bottom: 6, right: 6, borderBottomWidth: 2, borderRightWidth: 2, borderBottomRightRadius: '0.35rem' }
]

const QrScanFrame = ({
  children,
  framed = true,
  direction = 'column',
  gap = 0.375,
  height = '100%',
  maxHeight = 'none',
  active = true
}) => (
  <Box
    sx={(theme) => ({
      position: 'relative',
      flexGrow: 1,
      minWidth: 0,
      minHeight: 0,
      height,
      maxHeight,
      display: 'flex',
      flexDirection: direction,
      alignItems: 'center',
      justifyContent: 'center',
      gap,
      px: 2,
      py: 0.75,
      borderRadius: '0.75rem',
      overflow: 'hidden',
      backgroundColor: alpha(theme.palette.primary.main, 0.05)
    })}
  >
    {framed && CORNERS.map(({ id, ...corner }) => (
      <Box
        key={id}
        sx={(theme) => ({
          position: 'absolute',
          width: '1rem',
          height: '1rem',
          borderStyle: 'solid',
          borderWidth: 0,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          pointerEvents: 'none',
          ...corner
        })}
      />
    ))}

    {framed && active && (
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          left: '14%',
          right: '14%',
          height: '2px',
          borderRadius: '2px',
          background: 'linear-gradient(90deg, transparent, ' +
            alpha(theme.palette.primary.main, 0.5) + ', transparent)',
          animation: `${sweep} 3s ease-in-out infinite`,
          pointerEvents: 'none',
          '@media (prefers-reduced-motion: reduce)': { display: 'none' }
        })}
      />
    )}

    {children}
  </Box>
)

QrScanFrame.propTypes = {
  children: PropTypes.node,
  /** False drops the brackets and the sweep, keeping the surface. */
  framed: PropTypes.bool,
  direction: PropTypes.oneOf(['column', 'row']),
  gap: PropTypes.number,
  height: PropTypes.string,
  /** Past a point a frame stops reading as a target and starts reading as an empty box. */
  maxHeight: PropTypes.string,
  /** False stops the sweep: nothing is being waited for right now. */
  active: PropTypes.bool
}

export default QrScanFrame
